/**
 * When a live happens, as the host picks it: the day (from a small month), the
 * start, the length and the zone. New webinar draws these on its big card, and
 * "Change the time" (ChangeTime.tsx) draws the very same controls.
 */
import { useEffect, useRef, useState } from "react";
import { LIVE_LENGTHS, liveLengthChoices } from "../../shared/live-entry";
import { Select } from "../components/ui/Select";
import { TimeZonePicker } from "../components/TimeZonePicker";
import {
  dayLabel,
  daysAway,
  monthGrid,
  monthLabel,
  shiftMonth,
  startTimeGroups,
  todayIn,
  type LiveDraft,
} from "./liveForm";
import "../styles/stage.css";

const CHEVRON_LEFT = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" />
  </svg>
);
const CHEVRON_RIGHT = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** The four things that say when a live happens. */
export type LiveWhenValue = Pick<LiveDraft, "date" | "time" | "lengthMin" | "timezone">;

/** The small month the day is picked from. Past days cannot be picked. */
export function MonthPicker({
  value,
  today,
  onPick,
  onClose,
}: {
  value: string;
  today: string;
  onPick: (date: string) => void;
  onClose: () => void;
}) {
  const [month, setMonth] = useState(() => shiftMonth(value || today, 0));
  const box = useRef<HTMLDivElement>(null);
  const earliest = shiftMonth(today, 0);

  useEffect(() => {
    // The day's own button toggles the picker, so a press on it is not "away".
    const away = (e: MouseEvent) => {
      const area = box.current?.parentElement ?? box.current;
      if (area && !area.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);

  return (
    <div className="lcal" role="dialog" aria-label="Pick a day" ref={box}>
      <div className="lcalhead">
        <button
          type="button"
          aria-label="The month before"
          disabled={month <= earliest}
          onClick={() => setMonth(shiftMonth(month, -1))}
        >
          {CHEVRON_LEFT}
        </button>
        <b>{monthLabel(month)}</b>
        <button type="button" aria-label="The month after" onClick={() => setMonth(shiftMonth(month, 1))}>
          {CHEVRON_RIGHT}
        </button>
      </div>
      <div className="lcalgrid">
        {WEEKDAYS.map((d) => (
          <span key={d} className="wd" aria-hidden="true">
            {d.slice(0, 2)}
          </span>
        ))}
        {monthGrid(month).map((date, i) =>
          date ? (
            <button
              key={date}
              type="button"
              className={[date === value ? "sel" : "", date === today ? "today" : ""].filter(Boolean).join(" ")}
              disabled={date < today}
              aria-label={dayLabel(date)}
              aria-pressed={date === value}
              onClick={() => onPick(date)}
            >
              {Number(date.slice(8))}
            </button>
          ) : (
            <span key={`pad${i}`} aria-hidden="true" />
          ),
        )}
      </div>
    </div>
  );
}

/** The day, with how far away it is, then the start, the length and the zone. */
export function LiveWhenFields({
  value,
  onChange,
  keptLength,
}: {
  value: LiveWhenValue;
  onChange: (patch: Partial<LiveWhenValue>) => void;
  /** The length a live already runs, kept as a choice when the entry does not offer it. */
  keptLength?: number;
}) {
  const [picking, setPicking] = useState(false);
  const today = todayIn(value.timezone);
  return (
    <>
      <div className="ldate">
        <button
          type="button"
          className="ldaybtn"
          aria-haspopup="dialog"
          aria-expanded={picking}
          onClick={() => setPicking((open) => !open)}
        >
          {value.date ? dayLabel(value.date) : "Pick a day"}
        </button>
        {value.date ? <small>{daysAway(value.date, today)}</small> : null}
        {picking ? (
          <MonthPicker
            value={value.date}
            today={today}
            onPick={(date) => {
              onChange({ date });
              setPicking(false);
            }}
            onClose={() => setPicking(false)}
          />
        ) : null}
      </div>
      <div className="lrow">
        {/* A label around each dropdown, so pressing the small word opens it too. */}
        <label className="lsel">
          <small>Starts</small>
          <Select
            className="lselect"
            listClassName="lsel-list"
            ariaLabel="Start time"
            placeholder="Pick a time"
            value={value.time}
            suggested="09:00"
            options={startTimeGroups(value.date, value.timezone)}
            onChange={(time) => onChange({ time })}
          />
        </label>
        <label className="lsel">
          <small>For</small>
          <Select
            className="lselect"
            listClassName="lsel-list"
            ariaLabel="How long"
            value={String(value.lengthMin)}
            options={(keptLength ? liveLengthChoices(keptLength) : LIVE_LENGTHS).map((l) => ({ value: String(l.min), label: l.label }))}
            onChange={(min) => onChange({ lengthMin: Number(min) })}
          />
        </label>
        <label className="lsel">
          <small>Zone</small>
          <TimeZonePicker className="lselect" value={value.timezone} onChange={(timezone) => onChange({ timezone })} />
        </label>
      </div>
    </>
  );
}
